import { Button, Input, Modal, Select, Tag, message } from "antd";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import {
  getAllowedDomains,
  setAllowedDomains,
} from "../../../api/internal_tool_endpoints";
import {
  cleanDomains,
  isValidDomain,
  normalizeDomain,
} from "../../../utils/emailDomain";
import { foldSearch } from "../../../utils/searchFold";

/**
 * Manage a workspace's invite domain allowlist, opened from the
 * Workspace actions bar — a workspace picker plus the chip editor, mirroring
 * how Add Market / Invite Users pick their workspace inside the modal.
 *
 * Loads every workspace's current allowlist once on open (one bulk read), seeds
 * the chips when a workspace is picked, and writes with a per-workspace PUT.
 * Domains are normalized + de-duped client-side (matching the server); the
 * backend re-validates and is the source of truth. Empty = unrestricted.
 *
 * @param {boolean}  open
 * @param {string[]} workspaces  - id_clients to choose from.
 * @param {Function} onCancel
 * @param {Function} [onSaved]   - (idClient, storedDomains) after a save.
 */
const ManageAllowedDomainsModal = ({ open, workspaces, onCancel, onSaved }) => {
  const [byWorkspace, setByWorkspace] = useState({});
  const [loading, setLoading] = useState(false);
  const [idClient, setIdClient] = useState(null);
  const [domains, setDomains] = useState([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Load every workspace's current allowlist once when the modal opens.
  useEffect(() => {
    if (!open) return;
    setIdClient(null);
    setDomains([]);
    setInput("");
    setError("");
    setLoading(true);
    getAllowedDomains()
      .then((entries) => {
        const map = {};
        for (const entry of entries) {
          map[entry.id_client] = entry.allowed_domains || [];
        }
        setByWorkspace(map);
      })
      .catch((e) =>
        message.error(e.message || "Failed to load allowed domains."),
      )
      .finally(() => setLoading(false));
  }, [open]);

  const selectWorkspace = (ws) => {
    setIdClient(ws);
    setDomains(cleanDomains(byWorkspace[ws] || []));
    setInput("");
    setError("");
  };

  const addDomain = () => {
    const normalized = normalizeDomain(input);
    if (!normalized) return;
    if (!isValidDomain(normalized)) {
      setError(`"${input.trim()}" is not a valid email domain.`);
      return;
    }
    if (domains.includes(normalized)) {
      setError(`${normalized} is already listed.`);
      return;
    }
    setDomains([...domains, normalized]);
    setInput("");
    setError("");
  };

  const removeDomain = (domain) =>
    setDomains(domains.filter((d) => d !== domain));

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await setAllowedDomains({
        id_client: idClient,
        allowed_domains: domains,
      });
      const stored = response?.allowed_domains ?? cleanDomains(domains);
      setByWorkspace((m) => ({ ...m, [idClient]: stored }));
      message.success(`Allowed domains updated for ${idClient}.`);
      if (onSaved) onSaved(idClient, stored);
      onCancel();
    } catch (e) {
      message.error(e.message || "Failed to update allowed domains.");
    } finally {
      setSaving(false);
    }
  };

  const options = useMemo(
    () => (workspaces || []).map((w) => ({ value: w, label: w })),
    [workspaces],
  );

  return (
    <Modal
      title="Manage allowed email domains"
      open={open}
      onCancel={onCancel}
      width={560}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!idClient}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
    >
      <div className="mb-3">
        <div className="text-[0.8rem] text-zinc-500 mb-1">Workspace</div>
        <Select
          showSearch
          placeholder="Select a workspace"
          value={idClient}
          onChange={selectWorkspace}
          options={options}
          loading={loading}
          style={{ width: "100%" }}
          aria-label="Workspace"
          filterOption={(q, opt) =>
            foldSearch(opt?.label).includes(foldSearch(q))
          }
        />
      </div>

      {idClient ? (
        <>
          <p className="text-[0.8rem] text-zinc-500 mb-2">
            Only email addresses at these domains can be invited to this
            workspace. Leave empty to allow any domain. Subdomains do not
            inherit — list mail.acme.com separately from acme.com.
          </p>
          <div className="flex flex-wrap gap-2 min-h-[38px] p-2 border border-zinc-200 rounded-lg mb-2">
            {domains.length === 0 ? (
              <span className="text-[0.8rem] text-zinc-400 italic">
                No domains — any address may be invited (unrestricted).
              </span>
            ) : (
              domains.map((domain) => (
                <Tag
                  key={domain}
                  color="blue"
                  closable
                  onClose={(e) => {
                    e.preventDefault();
                    removeDomain(domain);
                  }}
                >
                  {domain}
                </Tag>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Add a domain, e.g. acme.com"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setError("");
              }}
              onPressEnter={addDomain}
            />
            <Button onClick={addDomain}>Add</Button>
          </div>
          {error ? (
            <div className="text-[0.75rem] text-red-600 mt-1">{error}</div>
          ) : null}
        </>
      ) : (
        <p className="text-[0.8rem] text-zinc-400 italic">
          Select a workspace to view and edit its allowed domains.
        </p>
      )}
    </Modal>
  );
};

ManageAllowedDomainsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  workspaces: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

export default ManageAllowedDomainsModal;
